import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Sparkles,
  Send,
  Bot,
  User,
  Wand2,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  MessageSquareText,
  Zap,
} from "lucide-react";
import { MeridianLogo } from "@/components/MeridianLogo";
import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { backendFetch } from "@/lib/backend";
import Loader from "@/components/ui/loader-4";
import { showError, showSuccess } from "@/utils/toast";
import {
  useGsapChatDynamics,
  useGsapTypingPulse,
  useGsapActionMagnet,
} from "@/hooks/useOrizonteMotion";
import {
  isSafeAssistantApiTarget,
  isSafeAssistantEvent,
  isSafeInternalRoute,
} from "@/lib/safe-actions";
import { SUBSCRIPTIONS_CHANGED_EVENT } from "@/lib/app-events";
import { sanitizeText } from "@/lib/sanitize";
import { useEntitlements } from "@/lib/entitlements";

type AssistantCard = {
  id: string;
  title: string;
  value: string;
  detail: string;
  tone: "neutral" | "good" | "warn" | "danger";
};

type AssistantAction = {
  id: string;
  label: string;
  type: "navigate" | "prompt" | "event" | "api";
  target: string;
  prompt?: string;
  method?: "PATCH" | "POST" | "DELETE";
  body?: Record<string, unknown>;
  confirm?: string;
};

type Message = {
  id: string;
  sender: "ai" | "user";
  text: string;
  isStreaming?: boolean;
  cards?: AssistantCard[];
  actions?: AssistantAction[];
};

const starterPrompts = [
  "What should I review first?",
  "Find overlap in my stack",
  "What renews soon?",
  "Where can I save money?",
];

type ParsedLine =
  | { type: "text"; text: string }
  | { type: "list"; text: string }
  | { type: "choice"; label: string; prompt: string };

type ParsedTable = {
  headers: string[];
  rows: string[][];
};

type ParsedSection = {
  id: string;
  title: string;
  lines: ParsedLine[];
  tables: ParsedTable[];
};

const cleanMarkdown = (value: string) =>
  sanitizeText(
    value
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/`(.*?)`/g, "$1")
    .replace(/^[-*]\s+/, "")
    .trim(),
    1_500
  );

const parseTable = (lines: string[], start: number) => {
  const tableLines: string[] = [];
  let index = start;
  while (index < lines.length && /^\s*\|.*\|\s*$/.test(lines[index])) {
    tableLines.push(lines[index]);
    index += 1;
  }

  if (tableLines.length < 2) return null;
  const headers = tableLines[0].split("|").slice(1, -1).map(cleanMarkdown);
  const rows = tableLines
    .slice(2)
    .map((line) => line.split("|").slice(1, -1).map(cleanMarkdown))
    .filter((row) => row.some(Boolean));

  return { table: { headers, rows }, nextIndex: index };
};

const parseAssistantResponse = (text: string) => {
  const normalized = text.replace(/\r/g, "").replace(/\s+---\s+/g, "\n---\n");
  const rawLines = normalized
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const lead: string[] = [];
  const sections: ParsedSection[] = [];
  let current: ParsedSection | null = null;

  const ensureSection = (title = "Summary") => {
    if (!current) {
      current = { id: `section-${sections.length}`, title, lines: [], tables: [] };
      sections.push(current);
    }
    return current;
  };

  let index = 0;
  while (index < rawLines.length) {
    const line = rawLines[index];
    if (line === "---") {
      index += 1;
      continue;
    }

    const table = parseTable(rawLines, index);
    if (table) {
      ensureSection().tables.push(table.table);
      index = table.nextIndex;
      continue;
    }

    const heading = line.match(/^#{2,4}\s*(.+)$/);
    if (heading) {
      current = {
        id: `section-${sections.length}`,
        title: cleanMarkdown(heading[1]),
        lines: [],
        tables: [],
      };
      sections.push(current);
      index += 1;
      continue;
    }

    const choice = line.match(/^(\d+)\.\s+(.+)$/);
    if (choice && current) {
      const label = cleanMarkdown(choice[2]).replace(
        /^(.+?)\s+[—-]\s+.*$/,
        "$1"
      );
      current.lines.push({ type: "choice", label, prompt: label });
      index += 1;
      continue;
    }

    const bullet = line.match(/^[-*]\s+(.+)$/);
    if (bullet) {
      ensureSection().lines.push({ type: "list", text: cleanMarkdown(bullet[1]) });
      index += 1;
      continue;
    }

    if (!current && lead.length < 2) {
      lead.push(cleanMarkdown(line));
    } else {
      ensureSection().lines.push({ type: "text", text: cleanMarkdown(line) });
    }
    index += 1;
  }

  return {
    lead,
    sections: sections.filter(
      (section) => section.lines.length > 0 || section.tables.length > 0
    ),
  };
};

/* ── Smart suggestion algorithm ── */
function getSmartSuggestions(messages: Message[]): string[] {
  if (messages.length === 0) return starterPrompts;

  const lastAi = [...messages].reverse().find((m) => m.sender === "ai");
  if (!lastAi) return starterPrompts;

  const text = lastAi.text.toLowerCase();
  const suggestions: string[] = [];

  if (text.includes("renewal") || text.includes("renew")) {
    suggestions.push("Show all upcoming renewals", "Set renewal reminders");
  }
  if (text.includes("overlap") || text.includes("duplicate")) {
    suggestions.push("Which subscriptions overlap?", "Merge duplicate tools");
  }
  if (
    text.includes("save") ||
    text.includes("waste") ||
    text.includes("money") ||
    text.includes("spend")
  ) {
    suggestions.push("Calculate potential savings", "Find unused subscriptions");
  }
  if (text.includes("category") || text.includes("breakdown")) {
    suggestions.push("Breakdown by category", "Compare monthly vs yearly");
  }
  if (text.includes("add") || text.includes("track")) {
    suggestions.push("Add a new subscription", "Import from CSV");
  }
  if (text.includes("calendar") || text.includes("date")) {
    suggestions.push("View renewal calendar", "Export to calendar");
  }
  if (suggestions.length === 0) return starterPrompts;
  return suggestions.slice(0, 4);
}

export const AIChatModal = () => {
  const navigate = useNavigate();
  const { hasFeature, requiredPlanFor } = useEntitlements();
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  useGsapChatDynamics(chatRef, [isOpen, messages.length, isTyping]);
  useGsapTypingPulse(chatRef, isTyping);
  useGsapActionMagnet(chatRef, "[data-gsap-magnet]", [
    isOpen,
    messages.length,
    isTyping,
  ]);

  const smartSuggestions = useMemo(
    () => getSmartSuggestions(messages),
    [messages]
  );
  const canUseMeridian = hasFeature("meridian_chat");

  useEffect(() => {
    const handleOpen = (e: Event) => {
      const customEvent = e as CustomEvent<{ prompt?: string }>;
      setIsOpen(true);
      if (customEvent.detail?.prompt) {
        setInput(customEvent.detail.prompt);
      }
      if (!canUseMeridian) return;
      if (messages.length === 0) {
        setMessages([
          {
            id: "welcome",
            sender: "ai",
            text: "Hello. I'm Meridian. I can inspect your saved Orizonte data for subscriptions, renewals, overlap, catalog alternatives, and potential cleanup.",
          },
        ]);
      }
    };
    document.addEventListener("open-ai-chat", handleOpen);
    return () => document.removeEventListener("open-ai-chat", handleOpen);
  }, [canUseMeridian, messages.length]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isTyping]);

  /* lock body scroll on mobile when open */
  useEffect(() => {
    if (isOpen && window.innerWidth < 640) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [isOpen]);

  /* close on outside click */
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        chatRef.current &&
        !chatRef.current.contains(target) &&
        !target.closest(".meridian-float-btn")
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleOutside);
      return () => document.removeEventListener("mousedown", handleOutside);
    }
  }, [isOpen]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    if (!canUseMeridian) {
      navigate(`/pricing?plan=${requiredPlanFor("meridian_chat")}`);
      return;
    }

    const userMsg = sanitizeText(input.trim(), 1_000);
    const payload = {
      message: userMsg,
      ...(conversationId ? { conversationId } : {}),
      history: messages.slice(-8).map(({ sender, text }) => ({ sender, text })),
    };
    setInput("");

    setMessages((prev) => [
      ...prev,
      { id: Date.now().toString(), sender: "user", text: userMsg },
    ]);
    setIsTyping(true);

    try {
      const data = await backendFetch("/assistant", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      setIsTyping(false);
      if (data.conversationId) setConversationId(String(data.conversationId));
      const cards = Array.isArray(data.cards)
        ? data.cards.map(sanitizeAssistantCard).filter(Boolean) as AssistantCard[]
        : [];
      const actions = Array.isArray(data.actions)
        ? data.actions.map(sanitizeAssistantAction).filter(Boolean) as AssistantAction[]
        : [];
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          sender: "ai",
          text: sanitizeText(data.reply || "", 6_000),
          cards,
          actions,
        },
      ]);
    } catch (error: unknown) {
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          sender: "ai",
          text:
            error instanceof Error
              ? error.message
              : "I could not reach the backend assistant right now.",
        },
      ]);
    }
  };

  const runAction = (action: AssistantAction) => {
    if (action.type === "navigate") {
      if (!isSafeInternalRoute(action.target)) {
        showError("Meridian blocked an unsafe navigation target");
        return;
      }
      setIsOpen(false);
      navigate(action.target);
      return;
    }
    if (action.type === "prompt" && action.prompt) {
      setInput(action.prompt);
      return;
    }
    if (action.type === "event") {
      if (!isSafeAssistantEvent(action.target)) {
        showError("Meridian blocked an unknown app event");
        return;
      }
      document.dispatchEvent(new CustomEvent(action.target));
    }
    if (action.type === "api") {
      if (!isSafeAssistantApiTarget(action)) {
        showError("Meridian blocked an unsupported API action");
        return;
      }
      const ok = action.confirm ? window.confirm(action.confirm) : true;
      if (!ok) return;
      backendFetch(action.target, {
        method: action.method || "POST",
        body: JSON.stringify(action.body || {}),
      })
        .then(() => {
          showSuccess(`${action.label} complete`);
          if (
            action.target === "/subscriptions" ||
            action.target.startsWith("/subscriptions/")
          ) {
            window.dispatchEvent(new CustomEvent(SUBSCRIPTIONS_CHANGED_EVENT));
          }
          setMessages((prev) => [
            ...prev,
            {
              id: `${Date.now()}-action`,
              sender: "ai",
              text: `${action.label} is complete. I refreshed your subscription telemetry so the dashboard and recommendations can catch up.`,
            },
          ]);
        })
        .catch((error) =>
          showError(error instanceof Error ? error.message : "Action failed")
        );
    }
  };

  const cardToneClass = {
    neutral: "border-white/5 bg-white/[0.02] text-orizonte-text",
    good: "border-orizonte-emerald/20 bg-orizonte-emerald/10 text-orizonte-emerald",
    warn: "border-yellow-500/20 bg-yellow-500/10 text-yellow-200",
    danger: "border-red-500/20 bg-red-500/10 text-red-200",
  };

  const actionIcon = (type: AssistantAction["type"]) => {
    if (type === "navigate") return <ExternalLink className="h-3.5 w-3.5" />;
    if (type === "api") return <CheckCircle2 className="h-3.5 w-3.5" />;
    if (type === "event") return <Sparkles className="h-3.5 w-3.5" />;
    return <MessageSquareText className="h-3.5 w-3.5" />;
  };

  const renderAssistantText = (msg: Message) => {
    const parsed = parseAssistantResponse(msg.text);

    if (parsed.sections.length === 0) {
      return (
        <div className="max-w-[92%] rounded-2xl border border-white/5 bg-white/[0.02] px-4 py-3 text-sm leading-relaxed text-orizonte-text">
          {parsed.lead.length ? parsed.lead.join(" ") : cleanMarkdown(msg.text)}
        </div>
      );
    }

    return (
      <div className="max-w-[92%] space-y-3">
        {parsed.lead.length > 0 && (
          <div className="rounded-2xl border border-orizonte-lime/15 bg-orizonte-lime/[0.04] px-4 py-3 text-sm leading-relaxed text-orizonte-text">
            {parsed.lead.map((line, index) => (
              <p
                key={`${msg.id}-lead-${index}`}
                className={index > 0 ? "mt-2 text-orizonte-textMuted" : ""}
              >
                {line}
              </p>
            ))}
          </div>
        )}

        {parsed.sections.map((section) => (
          <div
            key={`${msg.id}-${section.id}`}
            className="rounded-xl border border-white/[0.07] bg-white/[0.025] p-3.5"
          >
            <h3 className="text-[11px] font-semibold uppercase tracking-widest text-orizonte-lime/80">
              {section.title}
            </h3>
            <div className="mt-2 space-y-2">
              {section.lines.map((line, index) => {
                if (line.type === "choice") {
                  return (
                    <button
                      key={`${section.id}-choice-${index}`}
                      type="button"
                      data-chat-action
                      data-gsap-magnet
                      onClick={() => setInput(line.prompt)}
                      className="flex w-full items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-left text-xs font-medium text-orizonte-text transition-colors hover:border-orizonte-lime/30 hover:bg-orizonte-lime/10 hover:text-orizonte-lime"
                    >
                      <span>{line.label}</span>
                      <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                    </button>
                  );
                }
                if (line.type === "list") {
                  return (
                    <div
                      key={`${section.id}-list-${index}`}
                      className="flex gap-2 text-sm leading-relaxed text-orizonte-textMuted"
                    >
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-orizonte-lime/70" />
                      <span>{line.text}</span>
                    </div>
                  );
                }
                return (
                  <p
                    key={`${section.id}-text-${index}`}
                    className="text-sm leading-relaxed text-orizonte-textMuted"
                  >
                    {line.text}
                  </p>
                );
              })}
            </div>
            {section.tables.map((table, tableIndex) => (
              <div
                key={`${section.id}-table-${tableIndex}`}
                className="mt-3 overflow-hidden rounded-lg border border-white/[0.07]"
              >
                <div
                  className="grid bg-white/[0.04] text-[10px] font-semibold uppercase tracking-wider text-orizonte-textMuted"
                  style={{
                    gridTemplateColumns: `repeat(${table.headers.length}, minmax(0, 1fr))`,
                  }}
                >
                  {table.headers.map((header) => (
                    <div key={header} className="px-2.5 py-2">
                      {header}
                    </div>
                  ))}
                </div>
                {table.rows.map((row, rowIndex) => (
                  <div
                    key={`${section.id}-row-${rowIndex}`}
                    className="grid border-t border-white/5 text-xs text-orizonte-text"
                    style={{
                      gridTemplateColumns: `repeat(${table.headers.length}, minmax(0, 1fr))`,
                    }}
                  >
                    {table.headers.map((header, cellIndex) => (
                      <div
                        key={`${header}-${cellIndex}`}
                        className="px-2.5 py-2 leading-relaxed"
                      >
                        {row[cellIndex] || ""}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Floating AI Button */}
      <motion.button
        className={`meridian-float-btn relative w-14 h-14 rounded-full flex items-center justify-center cursor-pointer border-2 border-white/20 ${
          canUseMeridian
            ? "bg-gradient-to-br from-orizonte-lime to-orizonte-emerald text-black shadow-[0_0_24px_rgba(204,255,0,0.35)]"
            : "bg-[#111811] text-orizonte-textMuted shadow-[0_0_20px_rgba(204,255,0,0.08)]"
        }`}
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        animate={{ rotate: isOpen ? 90 : 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="relative z-10">
          {isOpen ? (
            <X className="w-7 h-7" />
          ) : (
            <MeridianLogo className="w-7 h-7" glow={false} />
          )}
        </div>
      </motion.button>

      {/* Chat Popup */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={chatRef}
            className="absolute bottom-20 right-0 w-[480px] max-w-[calc(100vw-2rem)] sm:w-[480px] sm:max-w-[calc(100vw-2rem)] max-sm:fixed max-sm:inset-0 max-sm:w-full max-sm:max-w-none max-sm:bottom-0 max-sm:right-0 max-sm:z-[60]"
            initial={{ opacity: 0, scale: 0.85, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 20 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
          >
            <div className="relative flex flex-col rounded-3xl max-sm:rounded-none bg-gradient-to-br from-[#0a100c]/95 to-[#040705]/95 border border-[#1a261e] max-sm:border-0 shadow-2xl backdrop-blur-3xl overflow-hidden max-h-[80vh] max-sm:max-h-[100dvh] max-sm:h-[100dvh]">
              {/* Header */}
              <div className="flex items-center justify-between px-5 pt-4 pb-2 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 bg-orizonte-lime/10 rounded-md border border-orizonte-lime/20">
                    <Sparkles className="w-4 h-4 text-orizonte-lime" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-orizonte-text">
                      Meridian
                    </h2>
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-orizonte-lime shadow-[0_0_5px_rgba(204,255,0,0.8)]" />
                      <span className="text-[10px] uppercase tracking-widest text-orizonte-lime/70 font-medium">
                        Agentic telemetry
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  data-gsap-magnet
                  className="p-2 text-orizonte-textMuted hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-4 min-h-[300px]">
                {!canUseMeridian && (
                  <div className="rounded-2xl border border-orizonte-lime/20 bg-orizonte-lime/[0.045] p-4">
                    <p className="text-xs font-semibold uppercase tracking-widest text-orizonte-lime">Pulse feature</p>
                    <h3 className="mt-2 text-base font-semibold text-orizonte-text">Meridian chat is locked</h3>
                    <p className="mt-2 text-sm leading-relaxed text-orizonte-textMuted">
                      Upgrade to Pulse to ask Meridian about overlaps, renewals, cleanup actions, and reporting.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate(`/pricing?plan=${requiredPlanFor("meridian_chat")}`)}
                      className="mt-4 inline-flex items-center gap-2 rounded-xl bg-orizonte-lime px-4 py-2.5 text-sm font-semibold text-black"
                    >
                      View plans
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {messages.map((msg) => (
                  <motion.div
                    data-chat-message
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    key={msg.id}
                    className={`flex gap-3 ${msg.sender === "user" ? "flex-row-reverse" : ""}`}
                  >
                    <div
                      className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                        msg.sender === "ai"
                          ? "bg-white/[0.03] border border-white/10"
                          : "bg-orizonte-lime/10 border border-orizonte-lime/20"
                      }`}
                    >
                      {msg.sender === "ai" ? (
                        <MeridianLogo className="w-3.5 h-3.5" glow={false} />
                      ) : (
                        <User className="w-3.5 h-3.5 text-orizonte-lime" />
                      )}
                    </div>
                    <div
                      className={`flex flex-col ${msg.sender === "user" ? "items-end" : "items-start"}`}
                    >
                      {msg.sender === "ai" ? (
                        renderAssistantText(msg)
                      ) : (
                        <div className="max-w-[88%] whitespace-pre-line rounded-2xl bg-orizonte-lime px-4 py-2.5 text-sm font-medium leading-relaxed text-black">
                          {msg.text}
                        </div>
                      )}
                      {msg.sender === "ai" && msg.cards && msg.cards.length > 0 && (
                        <div className="mt-2 grid w-full max-w-[92%] grid-cols-1 gap-2 sm:grid-cols-2">
                          {msg.cards.map((card) => (
                            <div
                              key={card.id}
                              data-chat-card
                              className={`rounded-xl border p-3 ${cardToneClass[card.tone]}`}
                            >
                              <p className="text-[10px] uppercase tracking-widest opacity-70">
                                {card.title}
                              </p>
                              <p className="mt-1 text-lg font-semibold">
                                {card.value}
                              </p>
                              <p className="mt-1 text-xs leading-relaxed text-orizonte-textMuted">
                                {card.detail}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                      {msg.sender === "ai" && msg.actions && msg.actions.length > 0 && (
                        <div className="mt-2 flex max-w-[92%] flex-wrap gap-2">
                          {msg.actions.map((action) => (
                            <button
                              key={action.id}
                              type="button"
                              data-chat-action
                              data-gsap-magnet
                              onClick={() => runAction(action)}
                              className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-medium text-orizonte-textMuted transition-colors hover:border-orizonte-lime/25 hover:bg-orizonte-lime/10 hover:text-orizonte-lime"
                            >
                              {actionIcon(action.type)}
                              {action.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}

                {isTyping && (
                  <motion.div
                    data-chat-message
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex gap-3"
                  >
                    <div className="w-7 h-7 rounded-lg bg-white/[0.03] border border-white/10 flex items-center justify-center shrink-0 mt-0.5">
                      <MeridianLogo className="w-3.5 h-3.5 animate-pulse" glow={false} />
                    </div>
                    <div className="px-4 py-2.5 rounded-2xl bg-white/[0.02] border border-white/5 flex items-center gap-2">
                      <Loader size={3} />
                      <span className="text-xs text-orizonte-textMuted">
                        Thinking
                      </span>
                      <span className="flex items-center gap-1" aria-hidden="true">
                        <span
                          data-typing-dot
                          className="h-1.5 w-1.5 rounded-full bg-orizonte-lime/80"
                        />
                        <span
                          data-typing-dot
                          className="h-1.5 w-1.5 rounded-full bg-orizonte-lime/60"
                        />
                        <span
                          data-typing-dot
                          className="h-1.5 w-1.5 rounded-full bg-orizonte-lime/40"
                        />
                      </span>
                    </div>
                  </motion.div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Smart Suggestions */}
              <div className="px-5 pb-2 shrink-0">
                <div className="flex flex-wrap gap-1.5">
                  {(canUseMeridian ? smartSuggestions : []).map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      data-chat-action
                      data-gsap-magnet
                      onClick={() => setInput(prompt)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-orizonte-lime/15 bg-orizonte-lime/[0.04] px-2.5 py-1.5 text-[11px] font-medium text-orizonte-textMuted transition-colors hover:border-orizonte-lime/30 hover:bg-orizonte-lime/10 hover:text-orizonte-lime"
                    >
                      <Zap className="h-3 w-3 text-orizonte-lime" />
                      <span>{prompt}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Input */}
              <div className="p-4 border-t border-[#1a261e]/50 bg-white/[0.01] shrink-0">
                <form onSubmit={handleSend} className="relative flex items-end gap-2">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend(e);
                      }
                    }}
                    rows={1}
                    placeholder={canUseMeridian ? "Ask about overlap, savings, or specific bills..." : "Upgrade to use Meridian"}
                    disabled={!canUseMeridian}
                    className="flex-1 min-h-[44px] max-h-[120px] bg-black/40 border border-white/10 rounded-xl pl-4 pr-3 py-3 text-sm text-orizonte-text placeholder:text-orizonte-textMuted focus:outline-none focus:border-orizonte-lime/50 transition-colors resize-none scrollbar-none"
                    style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                  />
                  <button
                    type="submit"
                    disabled={!canUseMeridian || !input.trim() || isTyping}
                    data-gsap-magnet
                    className="p-2.5 bg-orizonte-lime text-black rounded-xl disabled:opacity-50 disabled:bg-white/10 disabled:text-white/50 transition-colors shrink-0"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
                <div className="text-center mt-2">
                  <span className="text-[10px] text-orizonte-textMuted uppercase tracking-widest font-medium">
                    Uses your saved Orizonte data
                  </span>
                </div>
              </div>

              {/* Ambient overlay */}
              <div
                className="absolute inset-0 rounded-3xl pointer-events-none"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(204, 255, 0, 0.02), transparent, rgba(0, 229, 124, 0.02))",
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const sanitizeAssistantCard = (value: unknown): AssistantCard | null => {
  if (!value || typeof value !== "object") return null;
  const card = value as Partial<AssistantCard>;
  const tone = ["neutral", "good", "warn", "danger"].includes(String(card.tone))
    ? card.tone as AssistantCard["tone"]
    : "neutral";
  return {
    id: sanitizeText(card.id || crypto.randomUUID(), 120),
    title: sanitizeText(card.title, 120),
    value: sanitizeText(card.value, 120),
    detail: sanitizeText(card.detail, 500),
    tone,
  };
};

const sanitizeAssistantAction = (value: unknown): AssistantAction | null => {
  if (!value || typeof value !== "object") return null;
  const action = value as Partial<AssistantAction>;
  if (!["navigate", "prompt", "event", "api"].includes(String(action.type))) return null;
  return {
    id: sanitizeText(action.id || crypto.randomUUID(), 120),
    label: sanitizeText(action.label, 120),
    type: action.type as AssistantAction["type"],
    target: sanitizeText(action.target, 500),
    prompt: action.prompt ? sanitizeText(action.prompt, 1_000) : undefined,
    method: action.method,
    body: action.body,
    confirm: action.confirm ? sanitizeText(action.confirm, 500) : undefined,
  };
};
